import { computed } from "vue";
import { storeToRefs } from "pinia";

import { useDashboard } from "@/stores/dashboard/dashboard.store";
import { useLocalization } from "@/providers/Localization";
import { summarizeSelectedStages } from "@/features/Dashboard/stageFilterSummary";

import {
  bucketsForSelectedStages,
  buildHotspotRows,
  buildProjectMaterialRows,
  scopeRawStagesToSelection,
  type HotspotGroupBy,
  type HotspotRow,
} from "./hotspotBreakdown";

// Re-export the pure helpers so callers have a single entry point for the hotspot breakdown API.
export * from "./hotspotBreakdown";

// Thin composable: binds the pure helpers in ./hotspotBreakdown to the dashboard store's
// projectBreakdown tree + the localization provider (for translated material names).
export function useHotspotBreakdown() {
  const dashboard = useDashboard();
  const localization = useLocalization();
  const { projectBreakdown, unfilteredProjectBreakdown, emissionUnit, selectedStages, stages } = storeToRefs(dashboard);

  // The dashboard's own hotspot chart: the filtered breakdown, because honouring the model/stage
  // filters is the point of the chart.
  const getRows = (groupBy: HotspotGroupBy): HotspotRow[] =>
    buildHotspotRows(projectBreakdown.value, groupBy, (names, fallback) => localization.getEpdTranslation(names, fallback));

  // One row per project material — construction mappings included, keyed by their own id rather than
  // their layers'. Use this whenever the lookup key is a project material id (RTLCA-5346).
  //
  // Reads the *unfiltered* breakdown (RTLCA-5420): the caller is asking what one material
  // contributes to the project, which is not a question the dashboard's current filter selection
  // gets a say in — and under a deselected model the material isn't in the filtered tree at all.
  const getProjectMaterialRows = (): HotspotRow[] =>
    buildProjectMaterialRows(unfilteredProjectBreakdown.value, (names, fallback) =>
      localization.getEpdTranslation(names, fallback)
    );

  // The stage buckets the dashboard's global Stages filter leaves on the table. The breakdown data is
  // already scoped by that filter server-side, so a chart offering the other buckets is offering
  // stages it has no data for (RTLCA-5290).
  const globalStageBuckets = computed(() => bucketsForSelectedStages(selectedStages.value));

  // Narrow a row's raw stage map to the globally selected stages. A plain function rather than a
  // computed on purpose: callers invoke it from inside their own computeds, where reading
  // `selectedStages` at call time is what keeps them reactive to the filter. Callers that read the
  // *unfiltered* breakdown need this to honour the Stages filter at all (RTLCA-5397) — the filtered
  // breakdown arrives scoped server-side and must NOT be scoped again.
  const scopeToSelectedStages = (rawStages: Record<string, number> | undefined): Record<string, number> =>
    scopeRawStagesToSelection(rawStages, selectedStages.value);

  // What that scope is called on screen — the same label the filter bar's Stages button shows.
  // `ALL_STAGES_SUMMARY` when nothing is narrowed, which is the signal to say nothing at all.
  const stageScopeSummary = computed(() => summarizeSelectedStages(selectedStages.value, stages.value));

  return { getRows, getProjectMaterialRows, emissionUnit, globalStageBuckets, scopeToSelectedStages, stageScopeSummary };
}
